// Libs
import classNames from 'classnames/bind';
import { Heading, IconLinkLine, IconQuizLine, Table, Text, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, Loading } from '~/components/common';
import BaseDynamicSelect, {
  IBaseSelectPayload,
  IBaseSelectResult,
} from '~/components/common/specific/baseDynamicSelect/BaseDynamicSelect';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { IBaseSelectOption } from '~/utils/interface/common';
import { getCourses } from '~/thunks/course/courseThunk';
import { IListDataResponseCanvas } from '~/utils/interface/base';
import { IModuleDetail, IQueryGetModule } from '~/utils/interface/module';
import { getModules } from '~/thunks/module/moduleThunk';
import { MaterialTypeEnum } from '~/utils/enum/common';
import { teacherRoute } from '~/utils/routes/teacher';
// Styles, images, icons
import styles from './Material.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Material = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'teacher/material']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [modulesResponse, setModulesResponse] = useState<IListDataResponseCanvas<IModuleDetail[]> | null>(null);
  const [modules, setModules] = useState<IModuleDetail[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(false);
  const [courseSelected, setCourseSelect] = useState<string>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (courseSelected) {
      setPage(DEFAULT_PAGE);
      handleGetModules({
        limit: DEFAULT_LIMIT_PAGE,
        page: DEFAULT_PAGE,
        courseId: Number(courseSelected),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSelected]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const fetchCourseOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      try {
        const resp = await dispatch(getCourses({ page: payload.page, limit: DEFAULT_LIMIT_PAGE })).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch]
  );

  const handleGetModules = async (query: IQueryGetModule) => {
    setIsLoadingOverlay(true);
    await dispatch(getModules(query))
      .unwrap()
      .then((res) => {
        setModulesResponse(res.data ?? null);
        setModules(res.data?.responses ?? []);
      })
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleChangePage = (newPage: number) => {
    if (!courseSelected) return;

    setPage(newPage);
    handleGetModules({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
      courseId: Number(courseSelected),
    });
  };

  const renderModuleContent = () => {
    return (
      <>
        <View as='div' className={cx('listContainer', 'desktopOnly')}>
          {modules.length === 0 ? (
            <div className={cx('spinnerContainer')}>
              <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
            </div>
          ) : (
            <Table layout='auto'>
              <Table.Head>
                <Table.Row>
                  <Table.ColHeader id='Id' width='10%'>
                    {t('material_module_id_label', { ns: 'teacher/material' })}
                  </Table.ColHeader>
                  <Table.ColHeader id='Name' width='25%'>
                    {t('material_module_name_label', { ns: 'teacher/material' })}
                  </Table.ColHeader>
                  <Table.ColHeader id='Materials' width='65%'>
                    {t('material_module_materials_label', { ns: 'teacher/material' })}
                  </Table.ColHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {modules.map((data, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <span>{data.id}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span>{data.moduleName}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span>
                        <div className={cx('materialContainer')}>
                          {data.materials.map((material, index) => {
                            const isQuiz = material.type === MaterialTypeEnum.QUIZ;
                            const materialLink = isQuiz
                              ? `${teacherRoute.base}${teacherRoute.course}/${courseSelected}${teacherRoute.quiz}/${material.id}`
                              : material.link;

                            return (
                              <a
                                href={materialLink}
                                className={cx('itemLink')}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {material.type === MaterialTypeEnum.QUIZ ? <IconQuizLine /> : <IconLinkLine />}
                                <Text variant='content'>{material.title}</Text>
                              </a>
                            );
                          })}
                        </div>
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </View>

        {modules.length > 0 && (
          <View className={cx('paginationDesktop')}>
            <BasePagination currentPage={page} hasMore={modulesResponse?.hasMore} onChange={handleChangePage} />
          </View>
        )}

        <View as='div' className={cx('mobileOnly')}>
          {modules.length === 0 ? (
            <div className={cx('spinnerContainer')}>
              <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
            </div>
          ) : (
            <>
              {modules.map((data, index) => (
                <div key={index} className={cx('moduleCard')}>
                  <div className={cx('cardRow')}>
                    <strong>{t('material_module_id_label', { ns: 'teacher/material' })}:</strong>
                    <span>{data.id}</span>
                  </div>
                  <div className={cx('cardRow')}>
                    <strong>{t('material_module_name_label', { ns: 'teacher/material' })}:</strong>
                    <span>{data.moduleName}</span>
                  </div>
                  <div className={cx('cardRow', 'materialRow')}>
                    <strong>{t('material_module_materials_label', { ns: 'teacher/material' })}:</strong>
                    <div className={cx('materialContainer')}>
                      {data.materials.map((material, index) => {
                        const isQuiz = material.type === MaterialTypeEnum.QUIZ;
                        const materialLink = isQuiz
                          ? `${teacherRoute.base}${teacherRoute.course}/${courseSelected}${teacherRoute.quiz}/${material.id}`
                          : material.link;

                        return (
                          <a href={materialLink} className={cx('itemLink')} target='_blank' rel='noopener noreferrer'>
                            {material.type === MaterialTypeEnum.QUIZ ? <IconQuizLine /> : <IconLinkLine />}
                            <Text variant='content'>{material.title}</Text>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
              <BasePagination currentPage={page} hasMore={modulesResponse?.hasMore} onChange={handleChangePage} />
            </>
          )}
        </View>
      </>
    );
  };

  const renderContent = () => {
    return (
      <View as='div' width={'100%'} height={'100%'}>
        <View as='div' className={cx('courseSelect')}>
          <BaseDynamicSelect
            callApiFn={fetchCourseOptions}
            value={courseSelected}
            autoSelect
            onChange={(option) => setCourseSelect(option?.value)}
          />
        </View>

        {renderModuleContent()}
      </View>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('titleContainer')}>
          <div className={cx('headerTitle')}>
            <Heading level='h3'>{t('material_title', { ns: 'teacher/material' })}</Heading>
            <img
              className={cx('iconHeader')}
              src={icons.commonIconTuitionArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </div>
        </View>

        {renderContent()}
      </div>

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Material;
